import React, { useState, useEffect } from 'react'
import { API_BASE_URL } from '../config/api';
import { ArrowLeft, Database, Save, TestTube, Lock } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'

const DatabaseConfig = () => {
  const navigate = useNavigate()
  const [config, setConfig] = useState({
    db_type: 'sqlserver',
    db_server: '',
    db_port: 1433,
    db_name: 'pdf_signer',
    db_user: '',
    db_password: '',
    db_url: ''
  })
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [])

  const loadConfig = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/database`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      if (response.ok) {
        const data = await response.json()
        if (data) {
          setConfig({
            db_type: data.db_type || 'sqlserver',
            db_server: data.db_server || '',
            db_port: data.db_port || 1433,
            db_name: data.db_name || 'pdf_signer',
            db_user: data.db_user || '',
            db_password: '',
            db_url: data.db_url || ''
          })
        }
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error)
    }
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/database`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(config),
      })

      if (response.ok) {
        toast.success('Configuración de base de datos guardada exitosamente')
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al guardar configuración')
      }
    } catch (error) {
      toast.error('Error al guardar configuración')
    } finally {
      setLoading(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token')
      const response = await fetch(`${API_BASE_URL}/api/config/database/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        const result = await response.json()
        toast.success(result.message || 'Conexión a la base de datos exitosa')
      } else {
        const error = await response.json()
        toast.error(error.detail || 'Error al probar conexión')
      }
    } catch (error) {
      toast.error('Error al probar conexión')
    } finally {
      setTesting(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setConfig(prev => ({
      ...prev,
      [name]: name === 'db_port' ? parseInt(value) || 0 : value
    }))
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mr-4"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Volver</span>
        </button>
        <div className="flex items-center space-x-3">
          <Database className="h-6 w-6 text-primary-600" />
          <h1 className="text-2xl font-bold text-gray-900">Configuración de Base de Datos</h1>
        </div>
      </div>

      {/* Información */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <p className="text-sm text-gray-700">
              Configura la conexión a la base de datos del sistema. Puedes cambiar entre diferentes tipos de bases de datos según tus necesidades.
            </p>
          </div>
        </div>
      </div>

      {/* Formulario */}
      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Columna izquierda */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Base de Datos
              </label>
              <select
                name="db_type"
                value={config.db_type}
                onChange={handleChange}
                className="input-field"
              >
                <option value="sqlserver">SQL Server</option>
                <option value="postgresql">PostgreSQL</option>
                <option value="mysql">MySQL</option>
                <option value="sqlite">SQLite</option>
              </select>
            </div>

            {config.db_type !== 'sqlite' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Servidor
                  </label>
                  <input
                    type="text"
                    name="db_server"
                    value={config.db_server}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="192.168.29.138"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Puerto
                  </label>
                  <input
                    type="number"
                    name="db_port"
                    value={config.db_port}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="1433"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usuario
                  </label>
                  <input
                    type="text"
                    name="db_user"
                    value={config.db_user}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="sa"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre de la Base de Datos
              </label>
              <input
                type="text"
                name="db_name"
                value={config.db_name}
                onChange={handleChange}
                className="input-field"
                placeholder="pdf_signer"
              />
            </div>
          </div>

          {/* Columna derecha */}
          <div className="space-y-4">
            {config.db_type !== 'sqlite' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contraseña
                </label>
                <div className="relative">
                  <input
                    type="password"
                    name="db_password"
                    value={config.db_password}
                    onChange={handleChange}
                    className="input-field pl-10"
                    placeholder="Tu contraseña de base de datos"
                  />
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de Conexión (Opcional)
              </label>
              <input
                type="text"
                name="db_url"
                value={config.db_url}
                onChange={handleChange}
                className="input-field"
                placeholder="mssql+pymssql://user:pass@server:port/dbname"
              />
              <p className="text-xs text-gray-500 mt-1">
                Si se proporciona, se usará esta URL en lugar de los campos individuales
              </p>
            </div>
          </div>
        </div>

        {/* Botones */}
        <div className="flex items-center space-x-4 mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={loading}
            className="flex items-center space-x-2 bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="h-4 w-4" />
            <span>{loading ? 'Guardando...' : 'Guardar Configuración de Base de Datos'}</span>
          </button>

          <button
            onClick={handleTest}
            disabled={testing}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <TestTube className="h-4 w-4" />
            <span>{testing ? 'Probando...' : 'Probar Conexión'}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default DatabaseConfig
